import { thresholdLabel, thresholdUnit } from '../../../lib/alertRuleMeta';

const INPUT_CLS = 'min-h-[40px] w-full rounded-[12px] border border-line-strong bg-surface px-2.5 text-sm text-ink outline-none focus:ring-2 focus:ring-accent';
// Design `.th .field`: a 40px white field holding a mono input and the unit as a suffix.
const FIELD_CLS = 'flex min-h-[40px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-2.5 focus-within:ring-2 focus-within:ring-accent';
const NUM_INPUT_CLS = 'w-full min-w-0 border-0 bg-transparent p-0 font-mono text-sm text-ink outline-none placeholder:text-muted';
const LABEL_CLS = 'font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted';

interface Props {
  name: string;
  /** Prefix for element ids (the rule code), so cards sharing a threshold name don't duplicate ids. */
  idPrefix?: string;
  defaultValue: unknown;
  overrideValue: unknown;
  onChange: (next: unknown | undefined) => void;
}

function inferType(v: unknown): 'number' | 'string' | 'boolean' | 'array' | 'other' {
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string') return 'string';
  if (typeof v === 'boolean') return 'boolean';
  if (Array.isArray(v)) return 'array';
  return 'other';
}

function arrayToCsv(v: unknown[]): string {
  return v.map((x) => String(x)).join(', ');
}

function csvToArray(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

export default function ThresholdInput({ name, idPrefix, defaultValue, overrideValue, onChange }: Props) {
  const t = inferType(defaultValue);
  const fieldId = `${idPrefix ? `${idPrefix}-` : ''}th-${name}`;
  const label = thresholdLabel(name);
  const unit = thresholdUnit(name);
  const hasOverride = overrideValue !== undefined;
  const display = hasOverride ? overrideValue : '';

  if (t === 'number') {
    return (
      <div className="flex flex-col gap-1.5">
        <label className={LABEL_CLS} htmlFor={fieldId}>{label}</label>
        <span className={FIELD_CLS}>
          <input
            id={fieldId}
            type="number"
            aria-describedby={unit ? `${fieldId}-unit` : undefined}
            className={NUM_INPUT_CLS}
            placeholder={`Default ${String(defaultValue)}`}
            value={display as string | number}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return onChange(undefined);
              const n = Number(raw);
              if (Number.isFinite(n) && n === defaultValue) return onChange(undefined);
              onChange(Number.isFinite(n) ? n : undefined);
            }}
          />
          {unit && <span id={`${fieldId}-unit`} className="whitespace-nowrap text-xs font-medium text-muted">{unit}</span>}
        </span>
      </div>
    );
  }

  if (t === 'boolean') {
    const checked = hasOverride ? Boolean(overrideValue) : Boolean(defaultValue);
    return (
      <div className="flex items-center gap-2.5">
        <input
          type="checkbox"
          id={fieldId}
          className="h-4 w-4 accent-navy dark:accent-accent"
          checked={checked}
          onChange={(e) => {
            const next = e.target.checked;
            if (next === defaultValue) return onChange(undefined);
            onChange(next);
          }}
        />
        <label className="text-sm text-ink" htmlFor={fieldId}>{label}</label>
        <span className="text-xs text-muted">Default {String(defaultValue)}</span>
      </div>
    );
  }

  if (t === 'array') {
    const csv = hasOverride ? arrayToCsv(overrideValue as unknown[]) : '';
    return (
      <div className="flex flex-col gap-1.5">
        <label className={LABEL_CLS} htmlFor={fieldId}>{label}</label>
        <input
          id={fieldId}
          type="text"
          className={INPUT_CLS}
          placeholder={`Default ${arrayToCsv(defaultValue as unknown[])}`}
          value={csv}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw.trim() === '') return onChange(undefined);
            const arr = csvToArray(raw);
            if (JSON.stringify(arr) === JSON.stringify(defaultValue)) return onChange(undefined);
            onChange(arr);
          }}
        />
        <span className="text-xs text-muted">Comma-separated</span>
      </div>
    );
  }

  const isString = t === 'string';
  const displayStr = hasOverride
    ? (isString ? (overrideValue as string) : JSON.stringify(overrideValue))
    : '';
  const placeholder = isString ? String(defaultValue) : JSON.stringify(defaultValue);
  return (
    <div className="flex flex-col gap-1.5">
      <label className={LABEL_CLS} htmlFor={fieldId}>{label}</label>
      <input
        id={fieldId}
        type="text"
        className={INPUT_CLS}
        placeholder={`Default ${placeholder}`}
        value={displayStr}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(undefined);
          if (isString) {
            if (raw === defaultValue) return onChange(undefined);
            onChange(raw);
          } else {
            try {
              const parsed = JSON.parse(raw);
              if (JSON.stringify(parsed) === JSON.stringify(defaultValue)) return onChange(undefined);
              onChange(parsed);
            } catch {
              // ignore parse error
            }
          }
        }}
      />
    </div>
  );
}
